import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { UserPlus } from 'lucide-react';
import { DayHeader } from './DayHeader';
import { TimelineRow, type TimelineRowEntry } from './TimelineRow';
import { TimelineLoadingStrip } from './TimelineLoadingStrip';
import { CurrentTimeIndicator } from './CurrentTimeIndicator';
import { WeekendOverlay } from './WeekendOverlay';
import { useTimelineLayout } from './useTimelineLayout';
import {
  MEMBER_ROW_TINT,
  UNASSIGNED_ROW_ID,
  UNASSIGNED_ROW_LABEL,
  UNASSIGNED_ROW_TINT,
} from './rowTint';
import { NO_PROJECT_COLOR } from '@/utils/projectColor';

export interface TimelineEntry {
  id: string;
  title: string;
  date: string;
  endDate?: string | null;
  durationMinutes: number;
  // Nullable — see issue #10. A task without a project is rendered as
  // "No project" in the card.
  project: { id: string; name: string; color: string | null } | null;
  // 1 or 2 members — see issue #8. A pair-work entry shows up in both
  // member rows on the timeline (same card rendered twice).
  members: ReadonlyArray<{ id: string; name: string }>;
}

export interface TimelineUser {
  id: string;
  name: string;
}

interface TimelineGridProps {
  entries: TimelineEntry[];
  users: TimelineUser[];
  startDate: string;
  endDate: string;
  onEntryClick: (id: string) => void;
  onEntryContextMenu: (id: string, x: number, y: number) => void;
  /** Called when a card is dropped onto a different day. Receives the new date only —
   * endDate is intentionally NOT touched so the server preserves the original value. */
  onEntryMove: (id: string, date: string) => void;
  /** Called when the user scrolls close to the left or right edge of the
   * timeline. The consumer is expected to extend the [startDate, endDate]
   * window in the requested direction and re-fetch any data it needs.
   * Toggl uses this exact pattern for its "drag to load more days" feel. */
  onScrollEdge?: (edge: 'left' | 'right') => void;
  /** Called when the parent fails to refetch the extended range — gives
   * the consumer a chance to surface a toast and clear the loading
   * strip. The strip will keep showing otherwise. */
  onLoadError?: (edge: 'left' | 'right', error: Error) => void;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0] ?? '')
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/**
 * Custom Timeline grid that replaces vis-timeline. Layout is a CSS Grid with
 * a fixed-width label column on the left and one droppable cell per visible
 * day. Cards render absolutely inside their day cell and stack vertically
 * when more than one entry falls on the same day for the same user.
 */
export function TimelineGrid({
  entries,
  users,
  startDate,
  endDate,
  onEntryClick,
  onEntryContextMenu,
  onEntryMove,
  onScrollEdge,
  onLoadError,
}: TimelineGridProps) {
  const layout = useTimelineLayout(startDate, endDate);
  const {
    labelWidth,
    days,
    todayIndex,
    rowHeight,
    stackGap,
    totalDays,
    minDayWidth,
    headerHeight,
    isCompact,
  } = layout;

  const sensors = useSensors(
    // Activation distance 0: drag starts on pointerDown, no 4px threshold
    // (the 4px threshold made drag feel sluggish and users thought clicks
    // weren't being captured as drags).
    useSensor(PointerSensor, { activationConstraint: { distance: 0 } }),
  );

  // Track items-area width so the overlays / indicator line up with the
  // actual column widths (which depend on the container size).
  const itemsAreaRef = useRef<HTMLDivElement>(null);
  const [itemsAreaWidth, setItemsAreaWidth] = useState(0);

  useEffect(() => {
    const node = itemsAreaRef.current;
    if (!node) return;
    const update = () => setItemsAreaWidth(node.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Keep each sticky label the SAME height as its row (issue #33). Rows
  // grow when cards stack on the same day, but labels live in a separate
  // sticky column (issue #12) so they can't stretch naturally — without
  // this sync the tinted label block drifts out of alignment with the
  // tinted row block whenever a row is taller than `rowHeight`.
  const [rowHeights, setRowHeights] = useState<Record<string, number>>({});
  useEffect(() => {
    const root = itemsAreaRef.current;
    if (!root) return;
    const observer = new ResizeObserver((observed) => {
      setRowHeights((prev) => {
        const next = { ...prev };
        for (const entry of observed) {
          const target = entry.target as HTMLElement;
          const id = target.getAttribute('data-user-id');
          if (id) next[id] = target.offsetHeight;
        }
        return next;
      });
    });
    root
      .querySelectorAll('[data-testid="timeline-row"]')
      .forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [users, entries]);

  // Track timeline-root's left position in the viewport. The
  // loading strip is `position: fixed`, so the LEFT-edge strip needs
  // this offset to clear the page Sidebar (which sits at the leftmost
  // 256px of the viewport) and align with the right edge of the
  // sticky labels column. Updates on mount, on window resize, and on
  // any ancestor reflow that moves timeline-root.
  const [rootLeftOffset, setRootLeftOffset] = useState(0);
  useEffect(() => {
    const update = () => {
      const rect = layout.containerRef.current?.getBoundingClientRect();
      if (rect) setRootLeftOffset(rect.left);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [layout.containerRef]);

  // Day columns share the area after the 200px label column. With the
  // grid template `minmax(minDayWidth, 1fr)`, the actual rendered width
  // per day is at least minDayWidth — when the available space per day
  // is less, the grid scrolls horizontally (Toggl-style).
  const dayWidth =
    totalDays > 0 && itemsAreaWidth > labelWidth
      ? Math.max(minDayWidth, (itemsAreaWidth - labelWidth) / totalDays)
      : minDayWidth;

  // Scroll-driven lazy-load. When the user approaches the left or right
  // edge of the scrollable area, fire onScrollEdge so the consumer can
  // extend the date range in that direction.
  //
  // Loading state: when an edge fires, we set `loadingEdge` so the
  // <TimelineLoadingStrip> renders a Toggl-style placeholder at the
  // edge being loaded. The strip is cleared when the parent's
  // useLazyLoadQuery returns new data (handled in a useEffect on
  // startDate / endDate below).
  //
  // Scroll position preservation: we do NOT snap scrollLeft to
  // scrollWidth - clientWidth anymore (that was shifting today's
  // column off-screen on every load). Instead, when the parent
  // re-renders with new days, the new scrollWidth is larger; we
  // compensate by adding the scrollWidth delta to scrollLeft so the
  // user's current visible day stays at the same viewport-x. The
  // pendingBump ref captures the pre-load scrollWidth; the useEffect
  // applies the delta and clears the ref.
  //
  // Three guards to prevent the left edge from firing too eagerly (issue
  // #12 follow-up — left was "forced" because a tiny drag past the
  // threshold triggered an extension, and the snap-back caused another
  // extension, ad infinitum):
  //   1. Direction: only fire when the user is actively scrolling
  //      toward the relevant edge (direction > 0 for right, < 0 for left).
  //   2. Strict threshold: the left edge requires scrollLeft to be in
  //      the inner half of the threshold (e.g. < 128px instead of
  //      < 256px) so a tiny drag past the snap point doesn't re-fire.
  //   3. Cooldown: after a fire, wait EDGE_COOLDOWN_MS before firing
  //      again so a continuous drag doesn't extend the range N times.
  const prevScrollWidthRef = useRef(0);
  const prevScrollLeftRef = useRef(0);
  const preLoadItemsWidthRef = useRef(0);
  const lastEdgeFireAtRef = useRef(0);
  const [loadingEdge, setLoadingEdge] = useState<null | 'left' | 'right'>(null);
  // Watchdog: if the parent's refetch never resolves (e.g. network
  // failure with no onLoadError callback), drop the loading strip after
  // 30s so the user isn't stuck with a perpetual spinner.
  const LOAD_WATCHDOG_MS = 30_000;
  const EDGE_COOLDOWN_MS = 800;

  // Developer convenience: ?loadingDemo=right|left forces loadingEdge
  // on mount and KEEPS the strip visible (skipping the auto-clear in
  // the data-change useEffect below) so the developer can inspect
  // styles. Use ?loadingDemo=off to disable. Declared early (before
  // the useEffects that read it) to avoid the TDZ ReferenceError
  // when vitest renders the component before initialization.
  const loadingDemo = (() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const value = params.get('loadingDemo');
    return value === 'left' || value === 'right' || value === 'off' ? value : null;
  })();

  const handleScroll = useCallback(() => {
    const el = layout.containerRef.current;
    if (!el || !onScrollEdge) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const direction = scrollLeft - prevScrollLeftRef.current;
    const now = Date.now();
    const cooldownActive = now - lastEdgeFireAtRef.current < EDGE_COOLDOWN_MS;
    // EDGE_FIRE_PX is the buffer (in px) from each edge where the
    // lazy-load fires. 0 means "fire only at the very end" (issue
    // #12 follow-up: user wants the load to trigger at the END of
    // the scroll, not at 80% of the way).
    const EDGE_FIRE_PX = 0;

    // User is scrolling right (direction > 0) and at the right edge —
    // extend forward. No snap; the parent's useEffect will adjust
    // scrollLeft to keep today's column in place once the refetched
    // data lands.
    if (
      direction > 0 &&
      !cooldownActive &&
      scrollLeft + clientWidth >= scrollWidth - EDGE_FIRE_PX
    ) {
      lastEdgeFireAtRef.current = now;
      // Capture the items area width BEFORE the load so the strip
      // stays anchored to the boundary between loaded and loading,
      // not to the new (post-load) boundary.
      preLoadItemsWidthRef.current = itemsAreaRef.current?.scrollWidth ?? 0;
      setLoadingEdge('right');
      onScrollEdge('right');
    }
    // User is scrolling left (direction < 0) and at the left edge —
    // extend backward. No snap; same scrollLeft compensation.
    if (direction < 0 && !cooldownActive && scrollLeft <= EDGE_FIRE_PX) {
      lastEdgeFireAtRef.current = now;
      preLoadItemsWidthRef.current = itemsAreaRef.current?.scrollWidth ?? 0;
      setLoadingEdge('left');
      onScrollEdge('left');
    }

    prevScrollWidthRef.current = scrollWidth;
    prevScrollLeftRef.current = scrollLeft;
  }, [minDayWidth, onScrollEdge, layout.containerRef]);

  // Watchdog: drop the loading strip after LOAD_WATCHDOG_MS so a stuck
  // request doesn't pin a perpetual spinner. The parent can also call
  // onLoadError to drop it sooner (and surface a toast).
  //
  // Minimum display time: on localhost the round-trip is so fast that
  // the strip flashes for only a few ms and the user never sees it.
  // Forcing a minimum display window (MIN_DISPLAY_MS) matches the
  // Toggl feel — the strip is visible long enough for the user to
  // register that something is loading, even when the network is fast.
  // The useEffect on startDate/endDate also clears the strip, but it
  // respects this minimum so the strip stays visible for at least
  // MIN_DISPLAY_MS even when the response comes back instantly.
  const MIN_DISPLAY_MS = 400;
  const stripShownAtRef = useRef<number>(0);

  // Mount-time trigger for ?loadingDemo: force loadingEdge immediately
  // so the developer sees the strip on first paint. We track the
  // current value in a ref to avoid setState-during-render warnings.
  const loadingDemoRef = useRef(loadingDemo);
  useEffect(() => {
    loadingDemoRef.current = loadingDemo;
    if (loadingDemo === 'right' || loadingDemo === 'left') {
      stripShownAtRef.current = Date.now();
      setLoadingEdge(loadingDemo);
      // The loading strip is `position: fixed` (Toggl style) so it
      // stays anchored to the viewport regardless of scroll. No
      // auto-scroll needed for ?loadingDemo.
    }
  }, [loadingDemo]);

  // While a lazy-load is in progress, block horizontal scroll on the
  // root. The user must wait for the new days to land before they
  // can scroll past the current data — otherwise the visual context
  // shifts before the data is ready (issue #12 follow-up).
  useEffect(() => {
    const el = layout.containerRef.current;
    if (!el) return;
    el.style.overflowX = loadingEdge ? 'hidden' : '';
  }, [loadingEdge]);

  useEffect(() => {
    if (!loadingEdge) return;
    stripShownAtRef.current = Date.now();
    const id = setTimeout(() => {
      setLoadingEdge(null);
    }, LOAD_WATCHDOG_MS);
    return () => clearTimeout(id);
  }, [loadingEdge]);

  // After the date range widens, the parent re-renders us with new
  // startDate / endDate. We (1) adjust scrollLeft so today's column
  // stays at the same viewport-x, and (2) clear the loading strip. On
  // FIRST mount (no prior range), position the timeline so that desktop has
  // three days of breathing room before today while compact/mobile starts on
  // today itself — just to the right of the sticky labels column.
  //
  // Scroll-position compensation per edge (issue #13):
  // - LEFT edge: new days are PREPENDED. Without compensation, today
  //   would shift right by the new days' width and disappear off-screen.
  //   We add the scrollWidth delta to scrollLeft so today stays in
  //   place; the new days land off-screen LEFT and the user scrolls
  //   left to see them.
  // - RIGHT edge: new days are APPENDED. The user is already at (or
  //   near) the rightmost scroll position. Adding the delta would
  //   throw them into the middle of the new range. Instead we LEAVE
  //   scrollLeft untouched — the new days land off-screen RIGHT and
  //   the user scrolls right to see them.
  //
  // We track todayIdx as a ref so the effect re-runs when today is
  // finally identified (the days array can arrive empty on the first
  // render, before the today marker is computed).
  const lastTodayIdxRef = useRef<number>(-1);
  const lastInitialLayoutModeRef = useRef<boolean | null>(null);
  const lastRangeRef = useRef<{ start: string; end: string } | null>(null);
  const prevDaysCountRef = useRef<number>(days.length);
  useEffect(() => {
    const el = layout.containerRef.current;
    if (!el) return;
    const todayIdx = days.findIndex((d) => d.isToday);
    const prevRange = lastRangeRef.current;
    const initialLayoutModeChanged = lastInitialLayoutModeRef.current !== isCompact;

    if (
      todayIdx !== -1 &&
      (lastTodayIdxRef.current === -1 || initialLayoutModeChanged) &&
      loadingDemo !== 'right' &&
      loadingDemo !== 'left'
    ) {
      // First mount with today known — desktop keeps the "3 dias de
      // respiro" before today; compact/mobile puts today at the left edge.
      // The sticky labels column OVERLAPS the first `labelWidth` px of
      // the viewport, so the scroll offset is just targetIdx * dayWidth
      // — adding labelWidth here would push the target day UNDER the labels
      // column and show a later day at the left edge instead.
      // Skip when loadingDemo is forcing the right-edge strip so the
      // developer sees the loading position from the start.
      const breathingRoomDays = isCompact ? 0 : 3;
      const targetIdx = Math.max(0, todayIdx - breathingRoomDays);
      el.scrollLeft = targetIdx * minDayWidth;
    } else if (prevRange !== null && days.length > prevDaysCountRef.current) {
      // Range extended. Determine direction by comparing startDate:
      // decreased → left edge (prepend), unchanged/increased → right
      // edge (append).
      const wentLeft = startDate < prevRange.start;
      if (wentLeft) {
        // Prepend: today shifts right in scroll content by the added
        // days' width. Compensate by adding it to scrollLeft so today
        // stays in place; the new days land off-screen LEFT and the
        // user scrolls left to see them.
        //
        // We compute the delta from the DAYS COUNT × minDayWidth, NOT
        // from scrollWidth: while the lazy-load is in flight the items
        // area collapses to width 0 (loading state), so a
        // scrollWidth-based delta reads garbage and either misses the
        // compensation or double-applies it once real content returns.
        // (When the content overflows — the only situation where an
        // edge load can fire — dayWidth is exactly minDayWidth.)
        el.scrollLeft += (days.length - prevDaysCountRef.current) * minDayWidth;
      }
      // For right edge: leave scrollLeft alone. The new days land
      // off-screen right; the user scrolls right to see them.
    }

    // Only record todayIdx when today is actually in the range. Writing
    // -1 here (today temporarily missing while a refetch is in flight)
    // would re-arm the FIRST-MOUNT branch above: when today comes back
    // the effect would re-run the initial "today-3 at the left edge"
    // positioning and yank the scroll — on top of the prepend
    // compensation, that's exactly the double scroll jump users see.
    if (todayIdx !== -1) {
      lastTodayIdxRef.current = todayIdx;
      if (loadingDemo !== 'right' && loadingDemo !== 'left') {
        lastInitialLayoutModeRef.current = isCompact;
      }
    }
    prevDaysCountRef.current = days.length;
    lastRangeRef.current = { start: startDate, end: endDate };
    prevScrollWidthRef.current = el.scrollWidth;
    prevScrollLeftRef.current = el.scrollLeft;
    // Skip auto-clear when loadingDemo is active so the developer can
    // inspect styles without the strip disappearing.
    if (loadingDemo === 'right' || loadingDemo === 'left') {
      if (loadingEdge !== loadingDemo) {
        stripShownAtRef.current = Date.now();
        setLoadingEdge(loadingDemo);
      }
      return;
    }
    // Loading strip is done — new days are rendered. Respect the
    // MIN_DISPLAY_MS minimum so the strip stays visible long enough
    // for the user to register it (Toggl-style loading indicator).
    // If the response came back instantly, we wait the remaining time
    // before clearing the strip.
    if (loadingEdge) {
      const elapsed = Date.now() - stripShownAtRef.current;
      const remaining = Math.max(0, MIN_DISPLAY_MS - elapsed);
      setTimeout(() => setLoadingEdge(null), remaining);
    } else {
      setLoadingEdge(null);
    }
    // Depend on the date range and layout mode so the effect handles both
    // parent range extensions and the initial desktop-to-compact measurement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, layout.containerRef, days, minDayWidth, labelWidth, isCompact]);


  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) return;
      const dayData = over.data.current as { day?: string } | undefined;
      const targetDay = dayData?.day;
      if (!targetDay) return;

      const entryId = String(active.id);
      const original = entries.find((e) => e.id === entryId);
      if (!original) return;
      if (original.date === targetDay) return;

      onEntryMove(entryId, targetDay);
    },
    [entries, onEntryMove],
  );

  // Bucket entries per user for stable ordering in the rows. Pair-work
  // entries land in BOTH members' buckets so the card is visible from
  // each teammate's row — see issue #8. Entries with NO members land in
  // the Unassigned bucket (issue #33) — a task is not always attached to
  // a member at creation time; we attach it later, and until then the
  // card must still be visible on the timeline.
  const entriesByUser = useMemo(() => {
    const map = new Map<string, TimelineRowEntry[]>();
    for (const entry of entries) {
      const rowEntry: TimelineRowEntry = {
        id: entry.id,
        title: entry.title,
        projectName: entry.project?.name ?? 'No project',
        // Default to brand blue when the entry has no project (issue #10)
        // so the card is visually consistent with the rest of the timeline.
        projectColor: entry.project?.color ?? NO_PROJECT_COLOR,
        date: entry.date,
        endDate: entry.endDate,
        durationMinutes: entry.durationMinutes,
        userId: entry.members[0]?.id ?? '',
      };
      if (entry.members.length === 0) {
        const bucket = map.get(UNASSIGNED_ROW_ID) ?? [];
        bucket.push({ ...rowEntry, userId: UNASSIGNED_ROW_ID });
        map.set(UNASSIGNED_ROW_ID, bucket);
        continue;
      }
      for (const member of entry.members) {
        const bucket = map.get(member.id) ?? [];
        bucket.push({ ...rowEntry, userId: member.id });
        map.set(member.id, bucket);
      }
    }
    return map;
  }, [entries]);

  const unassignedEntries = entriesByUser.get(UNASSIGNED_ROW_ID) ?? [];
  const hasUnassigned = unassignedEntries.length > 0;

  // The labels live in a SEPARATE sticky column outside the rows
  // themselves. Putting them inside TimelineRow made `position: sticky`
  // bind to the row (or itemsAreaRef) instead of timeline-root, so the
  // label ended up anchored to the wrong edge and slid off-screen when
  // the user scrolled. Two parallel children of timeline-root fix this:
  // one is the scrolling items area, the other is a sticky labels
  // column pinned to the left edge (issue #12 — Toggl-style).
  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div
        ref={layout.containerRef}
        data-testid="timeline-root"
        onScroll={handleScroll}
        // The timeline-root is the horizontal scroll container with
        // `grid` so the labels column (200px) and itemsAreaRef (1fr)
        // sit side-by-side. `min-w-0` lets the grid column shrink so
        // the container stays at the parent's width even when the day
        // grid overflows.
        className="relative grid h-full max-w-full min-w-0 overflow-x-auto overflow-y-hidden bg-background"
        style={{
          gridTemplateColumns: `${labelWidth}px minmax(0, 1fr)`,
          // We compensate scrollLeft manually on left-edge prepends
          // (scrollWidth delta) — without this, Chrome's scroll anchoring
          // would ALSO adjust scrollLeft for the same prepend and today's
          // column would jump by exactly the prepended width.
          overflowAnchor: 'none',
        }}
      >
        {/* Labels column — sticky on the left. Sibling of itemsAreaRef so
            sticky binds to timeline-root directly. Vertically aligned to
            the rows by using the same rowHeight + border-b structure. */}
        <div
          data-testid="timeline-labels-column"
          className={`sticky left-0 z-20 shrink-0 bg-background ${
            isCompact ? 'border-r-2 border-border/80' : 'border-r'
          }`}
          style={{
            position: 'sticky',
            width: labelWidth,
            minWidth: labelWidth,
          }}
        >
          <div
            className={`flex items-center border-b bg-background text-xs font-medium text-muted-foreground ${
              isCompact ? 'justify-center px-1' : 'px-3'
            }`}
            // The DayHeader's bottom line is drawn by each day-header-cell's
            // border-b (container borders stop at the scrollport width), so
            // the header totals exactly `headerHeight` — match that here.
            style={{ minHeight: headerHeight }}
          >
            {/* empty header cell — matches the day-header height */}
          </div>
          {users.map((user, index) => (
            <div
              key={user.id}
              data-testid="timeline-row-label"
              // Same tint + gaps as the row cells so the label and the days
              // area read as ONE colored member block (issue #33). The mt on
              // the first block and the mb between blocks create the white
              // "respiro" bands that separate members, Toggl-style. Height
              // is synced to the row (which grows when cards stack) so the
              // tinted blocks never drift out of alignment.
              className={`mb-4 flex ${
                isCompact
                  ? 'flex-col items-center justify-center gap-1 overflow-hidden px-1 py-1 text-center'
                  : 'items-center gap-3 px-3 py-2'
              } ${MEMBER_ROW_TINT} ${index === 0 ? 'mt-4' : ''}`}
              style={{
                minHeight: rowHeight,
                height: rowHeights[user.id] ?? rowHeight,
              }}
            >
              <div
                data-testid="timeline-row-label-avatar"
                className={`flex shrink-0 items-center justify-center rounded-full bg-primary/20 font-bold text-primary ${
                  isCompact ? 'h-8 w-8 text-[10px]' : 'h-9 w-9 text-xs'
                }`}
              >
                {getInitials(user.name)}
              </div>
              <span
                data-testid="timeline-row-label-name"
                className={`min-w-0 font-medium text-foreground ${
                  isCompact
                    ? 'w-full truncate text-center text-[10px] leading-tight'
                    : 'text-sm'
                }`}
              >
                {user.name}
              </span>
            </div>
          ))}
          {hasUnassigned ? (
            <div
              data-testid="timeline-row-label-unassigned"
              className={`mb-4 flex ${
                isCompact
                  ? 'flex-col items-center justify-center gap-1 overflow-hidden px-1 py-1 text-center'
                  : 'items-center gap-3 px-3 py-2'
              } ${UNASSIGNED_ROW_TINT} ${users.length === 0 ? 'mt-4' : ''}`}
              style={{
                minHeight: rowHeight,
                height: rowHeights[UNASSIGNED_ROW_ID] ?? rowHeight,
              }}
            >
              <div
                data-testid="timeline-row-label-avatar"
                className={`flex shrink-0 items-center justify-center rounded-full bg-muted-foreground/20 text-muted-foreground ${
                  isCompact ? 'h-8 w-8' : 'h-9 w-9'
                }`}
              >
                <UserPlus className="h-4 w-4" />
              </div>
              <span
                data-testid="timeline-row-label-name"
                className={`min-w-0 font-medium text-muted-foreground ${
                  isCompact
                    ? 'w-full truncate text-center text-[10px] leading-tight'
                    : 'text-sm'
                }`}
              >
                {UNASSIGNED_ROW_LABEL}
              </span>
            </div>
          ) : null}
        </div>

        {/* Items area — scrolls horizontally. The day-header and rows are
            inside, aligned to the labels column by their natural layout.
            `relative` anchors the full-height day separators layer. */}
        <div
          ref={itemsAreaRef}
          className="relative h-full w-full"
        >
          <DayHeader
            days={days}
            minDayWidth={minDayWidth}
            headerHeight={headerHeight}
            isCompact={isCompact}
          />

          {/* Full-height day separators — one bordered column per day,
              stretched from below the header to the bottom of the items
              area. The day cells only draw their border-r inside user
              rows, so without this layer the vertical lines stop at the
              last row and leave the empty space below ungridded. Using
              the SAME grid template as the rows keeps every line
              pixel-aligned with the cells above (no pixel math). */}
          <div
            aria-hidden
            data-testid="timeline-day-separators"
            className="pointer-events-none absolute left-0 grid w-full"
            style={{
              top: headerHeight,
              bottom: 0,
              gridTemplateColumns: `repeat(${days.length}, minmax(${minDayWidth}px, 1fr))`,
            }}
          >
            {days.map((day) => (
              <div
                key={day.date}
                className={`border-r border-border/60 last:border-r-0 ${
                  isCompact ? 'border-dashed' : ''
                }`}
              />
            ))}
          </div>

          {/* Rows wrapper — the pt-4 leaves a white "respiro" band between
              the day header and the first member block (Toggl-style), and
              each row's mb-4 does the same between blocks. The matching
              gaps on the labels column keep label tops aligned with row
              tops. */}
          <div className="pt-4">
            {users.map((user) => (
              <TimelineRow
                key={user.id}
                userId={user.id}
                userName={user.name}
                userInitials={getInitials(user.name)}
                entries={entriesByUser.get(user.id) ?? []}
                layout={layout}
                tintClassName={MEMBER_ROW_TINT}
                onEntryClick={onEntryClick}
                onEntryContextMenu={onEntryContextMenu}
              />
            ))}
            {hasUnassigned ? (
              <TimelineRow
                userId={UNASSIGNED_ROW_ID}
                userName={UNASSIGNED_ROW_LABEL}
                userInitials=""
                entries={unassignedEntries}
                layout={layout}
                tintClassName={UNASSIGNED_ROW_TINT}
                onEntryClick={onEntryClick}
                onEntryContextMenu={onEntryContextMenu}
              />
            ) : null}
          </div>

          {/* Loading strip — Toggl-style placeholder that shows the user
              the lazy-load is in progress. Positioned in scroll content
              so it sits at the correct edge as the user scrolls. The
              strip's width is at least MIN_STRIP_WIDTH (the visual
              width of the strip) so it's visible even when the new
              scrollWidth hasn't grown yet (the request is still in
              flight at the moment we render). */}
        </div>

        {/* Loading strip — spinner bar rendered as a direct child of
            timeline-root (not inside itemsAreaRef) so it stays anchored
            to the visible edge of the timeline even when the user
            scrolls. Uses the same animate-spin ring as LoadingState. */}
        {loadingEdge ? (() => {
          const preItems = preLoadItemsWidthRef.current;
          const curItems = itemsAreaRef.current?.scrollWidth ?? 0;
          const loadedWidth = preItems > 0 ? preItems : curItems;
          const MIN_STRIP_WIDTH = 240;
          const startInScroll =
            loadingEdge === 'right' ? loadedWidth : 0;
          const endInScroll =
            loadingEdge === 'right'
              ? Math.max(curItems, loadedWidth + MIN_STRIP_WIDTH)
              : labelWidth;
          return (
            <TimelineLoadingStrip
              edge={loadingEdge}
              labelWidth={labelWidth}
              leftOffset={rootLeftOffset}
              startInScroll={startInScroll}
              endInScroll={endInScroll}
              headerHeight={headerHeight}
              rowHeight={rowHeight}
              rowCount={users.length + (hasUnassigned ? 1 : 0)}
            />
          );
        })() : null}

          {/* Overlays rendered as absolute children of the timeline-root
              (not itemsAreaRef) so they cover both the labels and the
              day columns. We need them positioned relative to the
              timeline-root, which has position:relative above. */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{ left: labelWidth, top: headerHeight }}
          >
            {days.map((day, index) =>
              day.isWeekend ? (
                <WeekendOverlay
                  key={day.date}
                  columnIndex={index}
                  dayWidth={dayWidth}
                  span={1}
                  leftOffset={0}
                />
              ) : null,
            )}
            <CurrentTimeIndicator
              todayIndex={todayIndex}
              dayWidth={dayWidth}
              leftOffset={0}
            />
          </div>
      </div>
    </DndContext>
  );
}
